import type { ImageModelId } from "@fitness-app/data-models/ai/models";
import { getImageModelSelectOptions, imageModelTasks } from "@fitness-app/data-models/ai/tasks";
import { useState } from "react";
import { DeleteOutlined, ExperimentOutlined, PictureOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Image, Input, message, Popover, Select, Skeleton, Space, Typography, Upload } from "antd";
import { type RcFile } from "antd/es/upload";

import { PermissionKey } from "@fitness-app/data-models";
import { CoverTarget, type CoverTargetEnum } from "@fitness-app/data-models/entities/ImageGeneration";
import { getErrorMessage } from "@fitness-app/utils";

import { usePermission } from "~/hooks/usePermission";
import { analyticsService, productApi } from "~/store/initializeStore";

// TRE-1857 — AI cover generator for products, the products counterpart of
// ChallengeCoverAiGenerator: same providers and reference-image input, but the checkout renders
// the product image as a square thumbnail, so the output is 1:1 instead of 16:9.
// TRE-1856 — also used for product resource thumbnails (`target="resource"`), which differ only in
// the wording and in the prompt the backend builds.

const COPY: Record<CoverTargetEnum, { popoverTitle: string; trigger: string; apply: string; missingContext: string }> =
  {
    [CoverTarget.PRODUCT]: {
      popoverTitle: "Generuj zdjęcie produktu",
      trigger: "Wygeneruj okładkę",
      apply: "Ustaw jako zdjęcie produktu",
      missingContext: "Najpierw wpisz nazwę produktu - model generuje obrazek na jej podstawie.",
    },
    [CoverTarget.PRODUCT_RESOURCE]: {
      popoverTitle: "Generuj miniaturkę materiału",
      trigger: "Wygeneruj miniaturkę",
      apply: "Ustaw jako miniaturkę",
      missingContext: "Najpierw wpisz tytuł materiału - model generuje obrazek na jego podstawie.",
    },
  };

export type ProductCoverContext = {
  name: string;
  description?: string | null;
  productType?: string | null;
};

/**
 * A getter is allowed for context that is not a form field - the resource editor pulls the
 * material's text out of EditorJS, which is only readable on demand.
 */
export type ProductCoverContextSource = ProductCoverContext | (() => ProductCoverContext);

type ReferenceImage = {
  data: string;
  mediaType: string;
  fileName: string;
};

type CoverModel = ImageModelId;

const MAX_REFERENCE_MB = 8;
const POPOVER_WIDTH = 460;
const PREVIEW_SIZE = 320;

const fileToBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Nie udało się odczytać pliku"));
    reader.readAsDataURL(file);
  });

const useProductCoverGenerator = () => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      productId,
      target,
      prompt,
      model,
      productContext,
      referenceImage,
    }: {
      productId: string;
      target: CoverTargetEnum;
      prompt: string;
      model?: CoverModel;
      productContext: ProductCoverContext;
      referenceImage?: ReferenceImage | null;
    }) => {
      setImageUrl(null);
      const response = await productApi.post<{ url: string }>("/generate-product-cover-image", {
        productId,
        target,
        prompt,
        model,
        productContext,
        referenceImage: referenceImage ? { data: referenceImage.data, mediaType: referenceImage.mediaType } : null,
      });
      setImageUrl(response.data.url);
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
    onSuccess: (_data, variables) => {
      // No raw prompt in analytics — it can contain client details; length is enough signal.
      analyticsService.track("generate_product_cover", {
        promptLength: variables.prompt.length,
        model: variables.model,
        target: variables.target,
        withReference: Boolean(variables.referenceImage),
      });
    },
  });

  return {
    mutation,
    isLoading: mutation.isLoading,
    imageUrl,
  };
};

export const ProductCoverAiGenerator = ({
  productId,
  productContext,
  setCoverUrl,
  target = CoverTarget.PRODUCT,
}: {
  productId: string;
  productContext: ProductCoverContextSource;
  setCoverUrl: (url: string) => void;
  target?: CoverTargetEnum;
}) => {
  const [visible, setVisible] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [reference, setReference] = useState<ReferenceImage | null>(null);
  const [model, setModel] = useState<CoverModel>(imageModelTasks.coverImage.default);

  const { mutation, isLoading, imageUrl } = useProductCoverGenerator();
  // The endpoint checks agents.use as well as the products permission (TRE-1961) — without the
  // key the generator can only 403, so it is not offered at all.
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const copy = COPY[target];
  const resolvedContext = typeof productContext === "function" ? productContext() : productContext;

  const onPickReference = async (file: RcFile) => {
    if (file.size / 1024 / 1024 > MAX_REFERENCE_MB) {
      message.error(`Obrazek referencyjny może mieć maksymalnie ${MAX_REFERENCE_MB}MB`);
      return Upload.LIST_IGNORE;
    }
    try {
      const data = await fileToBase64(file);
      setReference({ data, mediaType: file.type || "image/png", fileName: file.name });
    } catch (error) {
      message.error(getErrorMessage(error));
    }
    return false; // handled manually, no upload request
  };

  if (!canUseAgents) {
    return null;
  }

  return (
    <Popover
      open={visible}
      onOpenChange={setVisible}
      content={
        <Space direction="vertical" style={{ width: POPOVER_WIDTH, maxWidth: "100%" }}>
          <Input.TextArea
            value={prompt}
            placeholder="Wpisz dodatkowo instrukcje dla AI (opcjonalnie)"
            onChange={(e) => setPrompt(e.target.value || "")}
          />

          <Space.Compact block>
            <Upload accept="image/*" maxCount={1} showUploadList={false} beforeUpload={onPickReference}>
              {/* Upload mangles the file name into something long - it must not widen the popover. */}
              <Button icon={<PictureOutlined />} title={reference?.fileName} style={{ maxWidth: POPOVER_WIDTH - 40 }}>
                <span className="inline-block max-w-full truncate align-middle">
                  {reference ? `Referencja: ${reference.fileName}` : "Obrazek referencyjny (opcjonalnie)"}
                </span>
              </Button>
            </Upload>
            {reference ? (
              <Button icon={<DeleteOutlined />} onClick={() => setReference(null)} title="Usuń referencję" />
            ) : null}
          </Space.Compact>
          {reference ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Obrazek zostanie przekazany do modelu jako referencja stylu/kompozycji.
            </Typography.Text>
          ) : null}

          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Model
          </Typography.Text>
          <Select
            value={model}
            onChange={setModel}
            style={{ width: "100%" }}
            options={getImageModelSelectOptions("coverImage")}
          />

          <div className="flex items-center justify-end gap-3">
            {/* The only thing that disables Generate - say so, instead of leaving a dead button. */}
            {!resolvedContext.name ? (
              <Typography.Text type="warning" className="mr-auto text-xs">
                {copy.missingContext}
              </Typography.Text>
            ) : null}
            {imageUrl ? (
              <Button
                type="primary"
                onClick={() => {
                  setCoverUrl(imageUrl);
                  setVisible(false);
                }}
              >
                {copy.apply}
              </Button>
            ) : null}
            <Button
              type={imageUrl ? "default" : "primary"}
              loading={isLoading}
              disabled={!resolvedContext.name}
              onClick={() => {
                mutation.mutate({
                  productId,
                  target,
                  prompt,
                  model,
                  productContext: resolvedContext,
                  referenceImage: reference,
                });
              }}
            >
              {imageUrl ? "Generuj na nowo" : "Generuj"}
            </Button>
          </div>

          <div className="my-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-y-2">
                {/* 1:1 — the same square frame the checkout renders product images in */}
                <Skeleton.Image className="rounded-md" active style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }} />
                <Typography.Text type="secondary" className="text-center text-sm">
                  Generowanie może zająć nawet około 1 minutę...
                </Typography.Text>
              </div>
            ) : imageUrl ? (
              <div className="flex items-center justify-center">
                <Image
                  width={PREVIEW_SIZE}
                  height={PREVIEW_SIZE}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                  src={imageUrl}
                />
              </div>
            ) : null}
          </div>
        </Space>
      }
      title={copy.popoverTitle}
      trigger="click"
      placement="bottom"
    >
      <Button icon={<ExperimentOutlined />}>{copy.trigger}</Button>
    </Popover>
  );
};

export default ProductCoverAiGenerator;
